'use client';
import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Users,
  DollarSign,
  CheckCircle,
  XCircle,
  Loader2,
  Zap,
  Clock,
  Send,
} from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { calculateBookingQuote } from '@/lib/booking/pricing';

// ── Types ────────────────────────────────────────────────────────────────────
interface DayData {
  date: string; // yyyy-MM-dd
  isBooked: boolean;
  vendorCount: number;
  totalVendors: number;
  eventName?: string;
  eventType?: string;
}

interface LeadForm {
  name: string;
  email: string;
  phone: string;
  eventType: string;
  guestCount: string;
  venue: string;
  message: string;
}

const EVENT_TYPES = [
  'Wedding — Ceremony & Reception',
  'Wedding — Reception Only',
  'Karaoke Night',
  'Birthday Party',
  'Corporate Event',
  'Bingo Night',
  'Trivia Night',
  'Graduation Party',
  'Quinceañera',
  'Bar Event / Residency',
  'Other Private Event',
];

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

function toYMD(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function firstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

// ── Component ────────────────────────────────────────────────────────────────
export default function LiveBookingCalendar() {
  const [mounted, setMounted] = useState(false);
  const [viewYear, setViewYear] = useState(0);
  const [viewMonth, setViewMonth] = useState(0);
  const [dayMap, setDayMap] = useState<Record<string, DayData>>({});
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [totalVendors, setTotalVendors] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [todayStr, setTodayStr] = useState<string>('');

  const [form, setForm] = useState<LeadForm>({
    name: '', email: '', phone: '',
    eventType: '', guestCount: '', venue: '', message: '',
  });

  const supabase = createClient();

  // Initialize date on client only to prevent hydration mismatch
  useEffect(() => {
    const now = new Date();
    setViewYear(now.getFullYear());
    setViewMonth(now.getMonth());
    setTodayStr(toYMD(now));
    setMounted(true);
  }, []);

  // ── Fetch calendar data ───────────────────────────────────────────────────
  const fetchCalendarData = useCallback(async () => {
    if (!viewYear) return; // not yet mounted
    setLoading(true);
    try {
      const monthStart = new Date(viewYear, viewMonth, 1);
      const monthEnd = new Date(viewYear, viewMonth + 1, 0);
      const startStr = toYMD(monthStart);
      const endStr = toYMD(monthEnd);

      // Fetch booked events for this month
      const { data: events } = await supabase
        .from('events')
        .select('event_date, event_name, event_type, status')
        .gte('event_date', startStr)
        .lte('event_date', endStr)
        .in('status', ['upcoming', 'in_progress']);

      // Fetch total active vendors
      const { count: vendorCount } = await supabase
        .from('vendors')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      // Fetch vendor blocked dates overlapping this month
      const { data: blockedDates } = await supabase
        .from('vendor_blocked_dates')
        .select('vendor_id, start_date, end_date, block_type')
        .lte('start_date', endStr)
        .gte('end_date', startStr);

      const total = vendorCount || 0;
      setTotalVendors(total);

      // Build day map
      const map: Record<string, DayData> = {};
      const totalDays = daysInMonth(viewYear, viewMonth);

      for (let d = 1; d <= totalDays; d++) {
        const date = new Date(viewYear, viewMonth, d);
        const dateStr = toYMD(date);
        const dayOfWeek = date.getDay();

        // Count vendors blocked on this date
        const blockedVendorIds = new Set<string>();
        (blockedDates || []).forEach((b: any) => {
          if (b.block_type !== 'preferred' && dateStr >= b.start_date && dateStr <= b.end_date) {
            blockedVendorIds.add(b.vendor_id);
          }
        });

        const availableVendors = Math.max(0, total - blockedVendorIds.size);
        const bookedEvent = (events || []).find((e: any) => e.event_date === dateStr);

        map[dateStr] = {
          date: dateStr,
          isBooked: !!bookedEvent,
          vendorCount: availableVendors,
          totalVendors: total,
          eventName: bookedEvent?.event_name,
          eventType: bookedEvent?.event_type,
        };
      }

      setDayMap(map);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Calendar fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [viewYear, viewMonth]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  // ── Real-time subscription ────────────────────────────────────────────────
  useEffect(() => {
    const channel = supabase
      .channel('public-calendar-events')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'events' }, () => {
        fetchCalendarData();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchCalendarData]);

  // ── Navigation ────────────────────────────────────────────────────────────
  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  };

  // ── Day status helpers ────────────────────────────────────────────────────
  const isPast = (dateStr: string) => todayStr ? dateStr < todayStr : false;
  const isToday = (dateStr: string) => todayStr ? dateStr === todayStr : false;

  const getDayStatus = (dateStr: string): 'past' | 'booked' | 'limited' | 'available' => {
    if (isPast(dateStr)) return 'past';
    let d = dayMap[dateStr];
    if (!d) return 'available';
    if (d.isBooked) return 'booked';
    if (d.vendorCount < Math.ceil(d.totalVendors * 0.3)) return 'limited';
    return 'available';
  };

  // ── Quote preview ─────────────────────────────────────────────────────────
  const quote = calculateBookingQuote([]);

  // ── Form submit ───────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDate || !form.name || !form.email || !form.eventType) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await fetch('/api/booking-inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          eventType: form.eventType,
          eventDate: selectedDate,
          venue: form.venue,
          guestCount: form.guestCount,
          services: [],
          message: form.message,
        }),
      });
      const result = await res.json();
      if (!res.ok || !result.success) throw new Error(result.error || 'Submission failed');
      setSubmitted(true);
      fetchCalendarData();
    } catch (err: any) {
      setSubmitError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Build calendar grid ───────────────────────────────────────────────────
  const firstDay = mounted ? firstDayOfMonth(viewYear, viewMonth) : 0;
  const totalDays = mounted ? daysInMonth(viewYear, viewMonth) : 0;
  const cells: (number | null)[] = mounted
    ? [
        ...Array(firstDay).fill(null),
        ...Array.from({ length: totalDays }, (_, i) => i + 1),
      ]
    : [];
  // Pad to complete last row
  while (cells.length % 7 !== 0) cells.push(null);

  const selectedDayData = selectedDate ? dayMap[selectedDate] : null;
  const selectedStatus = selectedDate ? getDayStatus(selectedDate) : null;

  if (!mounted) {
    return (
      <section id="availability" className="py-24 relative">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <p className="text-xs font-mono tracking-[0.4em] text-muted-foreground uppercase mb-3">Live Availability</p>
            <h2 className="text-4xl md:text-5xl font-extrabold mb-4 gold-gradient-text">Check Your Date</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              See real-time availability, vendor capacity, and pricing — then book directly from the calendar.
            </p>
          </div>
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="availability" className="py-24 relative">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-xs font-mono tracking-[0.4em] text-muted-foreground uppercase mb-3">Live Availability</p>
          <h2 className="text-4xl md:text-5xl font-extrabold mb-4 gold-gradient-text">Check Your Date</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            See real-time availability, vendor capacity, and pricing — then book directly from the calendar.
          </p>
          {lastUpdated && (
            <div className="flex items-center justify-center gap-1.5 mt-3">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-xs text-muted-foreground font-mono">Live</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-5 gap-8">
          {/* ── Calendar ── */}
          <div className="xl:col-span-3 rounded-2xl neon-border card-glass overflow-hidden">
            {/* Month nav */}
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <button
                onClick={prevMonth}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/20 transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <div className="flex items-center gap-2">
                <Calendar size={15} className="text-primary" />
                <span className="text-sm font-bold text-foreground font-mono">
                  {MONTHS[viewMonth]} {viewYear}
                </span>
              </div>
              <button
                onClick={nextMonth}
                className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/20 transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 border-b border-border">
              {DAYS.map(d => (
                <div key={d} className="py-2 text-center text-xs font-semibold text-muted-foreground">
                  {d}
                </div>
              ))}
            </div>

            {/* Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 size={24} className="animate-spin text-primary" />
              </div>
            ) : (
              <div className="grid grid-cols-7">
                {cells.map((day, idx) => {
                  if (!day) {
                    return <div key={`empty-${idx}`} className="aspect-square border-b border-r border-border/30" />;
                  }
                  const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const status = getDayStatus(dateStr);
                  const isSelected = selectedDate === dateStr;
                  const dayData = dayMap[dateStr];

                  return (
                    <button
                      key={dateStr}
                      onClick={() => {
                        if (status === 'past') return;
                        setSelectedDate(dateStr);
                        setShowForm(false);
                        setSubmitted(false);
                        setSubmitError('');
                      }}
                      disabled={status === 'past'}
                      className={`
                        aspect-square border-b border-r border-border/30 flex flex-col items-center justify-center gap-0.5
                        transition-all text-xs font-semibold relative
                        ${status === 'past' ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/20'}
                        ${isSelected ? 'ring-2 ring-inset ring-primary bg-primary/10' : ''}
                        ${isToday(dateStr) && !isSelected ? 'bg-primary/5' : ''}
                      `}
                    >
                      <span className={`
                        ${isToday(dateStr) ? 'text-primary font-extrabold' : 'text-foreground'}
                        ${isSelected ? 'text-primary' : ''}
                      `}>
                        {day}
                      </span>
                      {status !== 'past' && (
                        <span className={`
                          w-1.5 h-1.5 rounded-full
                          ${status === 'booked' ? 'bg-red-400' : status === 'limited' ? 'bg-yellow-400' : 'bg-green-400'}
                        `} />
                      )}
                      {dayData?.isBooked && (
                        <span className="absolute top-0.5 right-0.5 text-[8px] text-red-400 font-mono">●</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Legend */}
            <div className="px-5 py-3 border-t border-border flex items-center gap-5 flex-wrap">
              {[
                { color: 'bg-green-400', label: 'Available' },
                { color: 'bg-yellow-400', label: 'Limited' },
                { color: 'bg-red-400', label: 'Booked' },
              ].map(({ color, label }) => (
                <div key={label} className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${color}`} />
                  <span className="text-xs text-muted-foreground">{label}</span>
                </div>
              ))}
              <div className="ml-auto flex items-center gap-1.5">
                <Users size={12} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">{totalVendors} vendors in network</span>
              </div>
            </div>
          </div>

          {/* ── Right Panel ── */}
          <div className="xl:col-span-2 flex flex-col gap-5">
            {/* Pricing card */}
            <div className="rounded-2xl neon-border-gold card-glass p-5">
              <div className="flex items-center gap-2 mb-4">
                <DollarSign size={15} className="text-primary" />
                <h3 className="text-sm font-bold text-foreground">Base Pricing</h3>
              </div>
              <div className="space-y-2.5">
                {[
                  { label: 'DJ ExXtra (Base)', price: '$800' },
                  { label: 'Photographer', price: '$800' },
                  { label: 'Videographer', price: '$900' },
                  { label: 'Bartender', price: '$350' },
                  { label: 'Photo Booth', price: '$500' },
                  { label: 'Live Musician', price: '$750' },
                ].map(({ label, price }) => (
                  <div key={label} className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{label}</span>
                    <span className="text-xs font-bold text-primary font-mono">{price}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-border">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Deposit (25%)</span>
                  <span className="text-xs font-bold text-foreground font-mono">${quote.depositAmount.toLocaleString()}</span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">+ 20% coordination fee on add-ons</p>
              </div>
            </div>

            {/* Selected date panel */}
            {selectedDate ? (
              <div className="rounded-2xl card-glass border border-border p-5 flex-1">
                {submitted ? (
                  <div className="flex flex-col items-center justify-center h-full gap-4 py-8">
                    <CheckCircle size={40} className="text-green-400" />
                    <div className="text-center">
                      <p className="text-sm font-bold text-foreground mb-1">Request Received!</p>
                      <p className="text-xs text-muted-foreground">
                        We'll contact you within 24 hours to confirm your booking for{' '}
                        <span className="text-primary font-semibold">{selectedDate}</span>.
                      </p>
                    </div>
                    <button
                      onClick={() => { setSubmitted(false); setSelectedDate(null); setShowForm(false); }}
                      className="text-xs text-primary hover:underline"
                    >
                      Check another date
                    </button>
                  </div>
                ) : !showForm ? (
                  <>
                    <div className="flex items-center gap-2 mb-4">
                      <Clock size={14} className="text-primary" />
                      <span className="text-sm font-bold text-foreground">{selectedDate}</span>
                    </div>

                    {selectedStatus === 'booked' ? (
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
                        <XCircle size={14} className="text-red-400 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-xs font-semibold text-red-400">Date Unavailable</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {selectedDayData?.eventType || 'An event'} is already booked on this date.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/20 mb-4">
                          <CheckCircle size={14} className="text-green-400 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-xs font-semibold text-green-400">
                              {selectedStatus === 'limited' ? 'Limited Availability' : 'Date Available!'}
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {selectedDayData?.vendorCount ?? totalVendors} vendor
                              {(selectedDayData?.vendorCount ?? totalVendors) !== 1 ? 's' : ''} available
                            </p>
                          </div>
                        </div>

                        <div className="mb-4 p-3 rounded-lg bg-muted/20 border border-border">
                          <div className="flex items-center gap-1.5 mb-2">
                            <Zap size={12} className="text-primary" />
                            <span className="text-xs font-semibold text-foreground">Instant Quote</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">Starting from</span>
                            <span className="text-sm font-extrabold text-primary font-mono">$1,200</span>
                          </div>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-muted-foreground">Deposit to hold date</span>
                            <span className="text-xs font-bold text-foreground font-mono">$300</span>
                          </div>
                        </div>

                        <button
                          onClick={() => setShowForm(true)}
                          className="w-full py-2.5 rounded-xl bg-primary text-black text-sm font-bold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                        >
                          <Send size={14} />
                          Book This Date
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  /* Booking form */
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-bold text-foreground">Book {selectedDate}</span>
                      <button
                        type="button"
                        onClick={() => setShowForm(false)}
                        className="text-xs text-muted-foreground hover:text-foreground"
                      >
                        ← Back
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="col-span-2">
                        <input
                          required
                          value={form.name}
                          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                          placeholder="Full Name *"
                          className="w-full px-3 py-2 rounded-lg bg-input border border-border text-xs text-foreground focus:outline-none focus:border-primary/50"
                        />
                      </div>
                      <input
                        required
                        type="email"
                        value={form.email}
                        onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                        placeholder="Email *"
                        className="w-full px-3 py-2 rounded-lg bg-input border border-border text-xs text-foreground focus:outline-none focus:border-primary/50"
                      />
                      <input
                        value={form.phone}
                        onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                        placeholder="Phone"
                        className="w-full px-3 py-2 rounded-lg bg-input border border-border text-xs text-foreground focus:outline-none focus:border-primary/50"
                      />
                      <div className="col-span-2">
                        <select
                          required
                          value={form.eventType}
                          onChange={e => setForm(f => ({ ...f, eventType: e.target.value }))}
                          className="w-full px-3 py-2 rounded-lg bg-input border border-border text-xs text-foreground focus:outline-none focus:border-primary/50"
                        >
                          <option value="">Event Type *</option>
                          {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <input
                        value={form.venue}
                        onChange={e => setForm(f => ({ ...f, venue: e.target.value }))}
                        placeholder="Venue"
                        className="w-full px-3 py-2 rounded-lg bg-input border border-border text-xs text-foreground focus:outline-none focus:border-primary/50"
                      />
                      <input
                        value={form.guestCount}
                        onChange={e => setForm(f => ({ ...f, guestCount: e.target.value }))}
                        placeholder="Guest Count"
                        className="w-full px-3 py-2 rounded-lg bg-input border border-border text-xs text-foreground focus:outline-none focus:border-primary/50"
                      />
                      <div className="col-span-2">
                        <textarea
                          value={form.message}
                          onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                          placeholder="Notes (optional)"
                          rows={2}
                          className="w-full px-3 py-2 rounded-lg bg-input border border-border text-xs text-foreground focus:outline-none focus:border-primary/50 resize-none"
                        />
                      </div>
                    </div>

                    {submitError && (
                      <p className="text-xs text-red-400">{submitError}</p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-2.5 rounded-xl bg-primary text-black text-sm font-bold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                    >
                      {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                      {submitting ? 'Submitting…' : 'Send Booking Request'}
                    </button>
                    <p className="text-[10px] text-muted-foreground text-center">
                      Instant lead capture — we'll confirm within 24 hours
                    </p>
                  </form>
                )}
              </div>
            ) : (
              <div className="rounded-2xl card-glass border border-border p-5 flex-1 flex flex-col items-center justify-center gap-3 text-center">
                <Calendar size={32} className="text-muted-foreground/40" />
                <p className="text-sm font-semibold text-foreground">Select a Date</p>
                <p className="text-xs text-muted-foreground max-w-[200px]">
                  Click any available date on the calendar to see vendor capacity and book instantly.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
